import * as API from 'core/api';
import { Bus } from 'core/infrastructure';

const process = Bus.buildProcessFn('/bus/');

export async function getBetweenBusinessDays(from: Date, to: Date): Promise<API.InvoiceInListDTO[]> {
	const request = new API.GetInvoicesByBusinesssDayRequest({ fromBusinessDay: from, toBusinessDay: to, includeFromOtherPointOfSales: true });
	return process(request, (response: API.GetInvoicesByBusinesssDayResponse) => response.Invoices);
}

export async function getByPosAndBusinessDay(businessDay: Date): Promise<API.InvoiceInListDTO[]> {
	const request = new API.GetInvoicesByBusinesssDayRequest({ fromBusinessDay: businessDay, toBusinessDay: businessDay, includeFromOtherPointOfSales: false });
	return process(request, (response: API.GetInvoicesByBusinesssDayResponse) => response.Invoices);
}

export async function getSeries() {

	const request = new API.GetAllSeriesRequest();
	const series = await process(request, (response: API.GetAllSeriesResponse) => response.All);

	return series
		.filter(x => [API.DocumentType.BasicInvoice, API.DocumentType.StandardInvoice, API.DocumentType.BasicRefund, API.DocumentType.StandardRefund].includes(x.Type))
		.sortBy(x => x.IsInUse, 'desc', x => x.Id, 'asc');
}

export async function getBySerieAndNumber(invoiceSerie: string, invoiceNumber: number): Promise<API.InvoiceInListDTO> {
	const request = new API.GetInvoiceDetailsBySerieAndNumberRequest({ serie: invoiceSerie, number: invoiceNumber });
	return process(request, (response: API.GetInvoiceDetailsBySerieAndNumberResponse) => response.Details.Invoice);
}

export async function getById(invoiceId: number): Promise<API.InvoiceDTO> {
	const request = new API.GetInvoiceByIdRequest({ invoiceId });
	return process(request, (response: API.GetInvoiceByIdResponse) => response.Invoice);
}

export async function getByTicketId(ticketId: number): Promise<API.InvoiceDTO> {
	const request = new API.GetInvoiceByTicketIdRequest({ ticketId });
	return process(request, (response: API.GetInvoiceByTicketIdResponse) => response.Invoice);
}

export function getDocumentPayments(invoiceId: number): Promise<API.DocumentPaymentDTO[]> {
	const request = new API.GetInvoiceDocumentPaymentsRequest({ invoiceId });
	return process(request, (response: API.GetInvoiceDocumentPaymentsResponse) => response.Payments);
}

export function getOriginalPaymentsToRefund(invoiceId: number): Promise<API.PaymentDTO[]> {
	const request = new API.GetOriginalInvoicePaymentsToRefundRequest({ invoiceId });
	return process(request, (response: API.GetOriginalInvoicePaymentsToRefundResponse) => response.Payments);
}

export async function hasAlreadyBeenRefunded(invoiceId: number): Promise<boolean> {
	const request = new API.HasAlreadyBeenRefundedRequest({ invoiceId });
	return process(request, (response: API.HasAlreadyBeenRefundedResponse) => response.HasBeenRefunded);
}

export async function getEmailOptions(invoiceId: number): Promise<API.DocumentEmailOptionsDTO> {
	const request = new API.GetInvoiceEmailOptionsRequest({ invoiceId });
	return process(request, (response: API.GetInvoiceEmailOptionsResponse) => response.Options);
}

export async function sendEmail(options: API.DocumentEmailOptionsDTO): Promise<API.SendDocumentEmailResultDTO> {
	const request = new API.SendInvoiceEmailRequest({ options });
	return process(request, (response: API.SendInvoiceEmailResponse) => response.Result);
}

export async function createTipOperation(invoiceId: number): Promise<API.TipOperationDTO> {
	const request = new API.CreateTipOperationRequest({ invoiceId });
	return process(request, (response: API.CreateTipOperationResponse) => response.Operation);
}

export async function reopen(invoiceId: number, location: API.TicketLocationDTO, targetTicketId: Nullable<number> = null): Promise<API.TicketDTO> {
	const request = new API.ReopenInvoiceFromPosRequest({ invoiceId, params: { Location: location, TargetTicketId: targetTicketId } });
	return process(request, (response: API.ReopenInvoiceFromPosResponse) => response.Ticket);
}

export async function convertToStandard(invoiceId: number, customer: API.BuyerCustomerDTO): Promise<void> {
	const request = new API.ConvertToStandardInvoiceFromPosRequest({
		basicInvoiceId: invoiceId,
		params: { Customer: customer }
	});
	await process(request);
}

export async function addTip(invoiceId: number, tip: API.TipPaymentDTO): Promise<API.TipPaymentDTO[]> {
	const request = new API.AddTipRequest({ invoiceId, tip });
	return process(request, (response: API.AddTipResponse) => response.Tips);
}

export async function setCustomer(invoiceId: number, customer: API.BuyerCustomerDTO): Promise<void> {
	const request = new API.SetInvoiceBuyerCustomerRequest({ invoiceId, customer });
	await process(request);
}

export async function updateCustomerData(invoiceId: number): Promise<void> {
	const request = new API.UpdateCustomerDataFromInvoiceRequest({ invoiceId });
	await process(request);
}

export async function refund(invoiceId: number, params: API.InvoiceParamsFromPosDTO): Promise<API.SaleDTO> {
	const request = new API.RefundInvoiceFromPosRequest({ invoiceId, params });
	return process(request, (response: API.RefundInvoiceFromPosResponse) => response.Sale);
}

export function changePayment(invoiceId: number, params: API.InvoiceParamsFromPosDTO) {
	const request = new API.ChangeInvoicePaymentFromPosRequest({ invoiceId, params });
	return process(request, (response: API.ChangeInvoicePaymentFromPosResponse) => response.Sale);
}

export async function checkRequiredCustomer(invoiceId: number, payments: API.PaymentDTO[]): Promise<API.CheckRequiredCustomerResultDTO> {
	const request = new API.CheckRequiredCustomerForInvoiceRequest({ invoiceId, newPayments: payments });
	return await process(request, (response: API.CheckRequiredCustomerForInvoiceResponse) => response.Result);
}

export async function getTicketGlobalIdByInvoiceId(invoiceId: number): Promise<Nullable<string>> {
	const request = new API.GetTicketGlobalIdByInvoiceIdRequest({ invoiceId });
	return await process(request, (response: API.GetTicketGlobalIdByInvoiceIdResponse) => response.GlobalId);
}

export async function getLastCashAmountToRefund(invoiceId: number): Promise<number> {
	const request = new API.GetLastCashAmountToRefundFromInvoiceRequest({ invoiceId });
	return await process(request, (response: API.GetLastCashAmountToRefundFromInvoiceResponse) => response.Amount);
}

export async function getLines(invoiceId: number): Promise<API.TicketLineDTO[]> {
	const request = new API.GetAllLinesInInvoiceRequest({ invoiceId });
	return await process(request, (response: API.GetAllLinesInInvoiceResponse) => response.Lines);
}

export async function createTicketFromInvoice(invoiceId: number): Promise<API.TicketDTO> {
	const request = new API.CreateTicketFromRefundRequest({ invoiceId });
	return await process(request, (response: API.CreateTicketFromRefundResponse) => response.Ticket);
}

export async function deleteTicketFromInvoice(ticketId: number): Promise<void> {
	const request = new API.DeleteTicketFromRefundRequest({ ticketId });
	await process(request);
}

export async function createPartialInvoiceRefund(ticket: API.TicketDTO, params: API.InvoiceParamsFromPosDTO): Promise<API.SaleDTO> {
	const request = new API.RefundTicketFromPosRequest({ ticketId: ticket.Id, params, isInvoicePartialRefund: true });
	return await process(request, (response: API.RefundTicketFromPosResponse) => response.Sale);
}

export async function setTableCompanions(invoiceId: number, tableCompanions: Nullable<number>) {
	const request = new API.SetInvoiceTableCompanionsRequest({ invoiceId, tableCompanions });
	return await process(request);
}
